import { useState } from 'react';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';

export interface ActivationPaymentSettings {
    id: number;
    activationId: number;
    costInCents: number;
    stripePublishableKey?: string;
    stripeEnvironment?: string;
    stripeMode?: "inherit" | "override";
    stripeSource?: string;
    taxEnabled?: boolean;
    taxRatePercent?: string;
    stripeWebhookSecret?: string;
    hasStripeKeys: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface CreatePaymentSettingsRequest {
    activationId: number;
    costInCents: number;
    stripePublishableKey?: string;
    stripeSecretKey?: string;
    stripeEnvironment?: string;
    stripeMode?: "inherit" | "override";
    stripeSource?: string;
    taxEnabled?: boolean;
    taxRatePercent?: string;
    stripeWebhookSecret?: string;
}

export interface UpdatePaymentSettingsRequest {
    activationId: number;
    costInCents: number;
    stripePublishableKey?: string;
    stripeSecretKey?: string;
    stripeEnvironment?: string;
    stripeMode?: "inherit" | "override";
    stripeSource?: string;
    taxEnabled?: boolean;
    taxRatePercent?: string;
    stripeWebhookSecret?: string;
}

export interface PaymentReportData {
    salesSubtotal?: number; salesTax?: number; grossCollected?: number;
    refundedTotal?: number; refundedTax?: number; netCollected?: number;
    paymentsLast24h: number;
    revenueLast24h: number;
    paymentsCurrentWeek: number;
    revenueCurrentWeek: number;
    paymentsPreviousWeek: number;
    revenuePreviousWeek: number;
    paymentsCurrentMonth: number;
    revenueCurrentMonth: number;
    paymentsLifetime: number;
    revenueLifetime: number;
    totalClaimed: number;
    totalInvalidated: number;
    totalSessions: number;
    lastSessionDate: string | null;
    dailyChartData: Array<{
        date: string;
        count: number;
        revenue: number;
    }>;
}

export interface PaymentRecord {
    events?: Array<{ id: number; type: string; createdAt: string; data: { screenId?: string; resolution?: string; refundedCents?: number } }>;
    subtotalCents?: number; taxCents?: number; totalCents?: number;
    refundedCents?: number; paymentStatus?: string; recoveryRequired?: boolean;
    fulfillment?: Record<string, string>;
    id: number;
    photoSessionId: string;
    activationId: number;
    galleryId: number;
    redemptionCode: string;
    costInCents: number;
    paymentIntentId: string;
    claimedAt: string;
    paidAt: string;
    invalidatedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export default function useActivationPaymentSettings() {
    const authenticatedApi = useAuthenticatedApi();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const getActivationPaymentSettings = async (
        activationId: number,
    ): Promise<ActivationPaymentSettings | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.get(
                `/activations/${activationId}/paymentSettings`,
            );
            return response.data;
        } catch (err: any) {
            // If 404, it means payment settings don't exist yet - this is expected
            if (err.response?.status === 404) {
                return null;
            }
            // If 401, it means the user doesn't have admin privileges - throw the error
            if (err.response?.status === 401) {
                throw err;
            }
            console.error('Error fetching activation payment settings:', err);
            setError(
                err.response?.data?.message ||
                    'Failed to fetch payment settings',
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const createActivationPaymentSettings = async (
        data: CreatePaymentSettingsRequest,
    ): Promise<ActivationPaymentSettings | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.post(
                `/activations/${data.activationId}/paymentSettings`,
                data,
            );
            return response.data;
        } catch (err: any) {
            console.error('Error creating activation payment settings:', err);
            setError(
                err.response?.data?.message ||
                    'Failed to create payment settings',
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const updateActivationPaymentSettings = async (
        data: UpdatePaymentSettingsRequest,
    ): Promise<ActivationPaymentSettings | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.put(
                `/activations/${data.activationId}/paymentSettings`,
                data,
            );
            return response.data;
        } catch (err: any) {
            console.error('Error updating activation payment settings:', err);
            setError(
                err.response?.data?.message ||
                    'Failed to update payment settings',
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const getPaymentReportData = async (
        activationId: number,
        startDate?: string,
        endDate?: string,
    ): Promise<PaymentReportData | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);

            const response = await authenticatedApi.get(
                `/activations/${activationId}/paymentSettings/report?${params.toString()}`,
            );
            return response.data;
        } catch (err: any) {
            // If 401, it means the user doesn't have admin privileges - throw the error
            if (err.response?.status === 401) {
                throw err;
            }
            console.error('Error fetching payment report data:', err);
            setError(
                err.response?.data?.message ||
                    'Failed to fetch payment report data',
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const getPaymentRecords = async (
        activationId: number,
        startDate?: string,
        endDate?: string,
    ): Promise<PaymentRecord[]> => {
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);

            const response = await authenticatedApi.get(
                `/activations/${activationId}/paymentSettings/records?${params.toString()}`,
            );
            return response.data || [];
        } catch (err: any) {
            // If 401, it means the user doesn't have admin privileges - throw the error
            if (err.response?.status === 401) {
                throw err;
            }
            console.error('Error fetching payment records:', err);
            setError(
                err.response?.data?.message ||
                    'Failed to fetch payment records',
            );
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    return {
        getActivationPaymentSettings,
        createActivationPaymentSettings,
        updateActivationPaymentSettings,
        getPaymentReportData,
        getPaymentRecords,
        isLoading,
        error,
    };
}
